import React, { useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  Grid,
  Grow,
  Icon,
  Typography,
  useTheme,
} from "@material-ui/core";
import _ from "lodash";

import {
  DASHBOARD_CARD_EXTRA,
  DASHBOARD_CARD_EXTRA_LARGE,
  DASHBOARD_CARD_LARGE,
  DASHBOARD_CARD_MEDIUM,
} from "../../../../util/Constants";
import { useStyles } from "./HomeCardStyles";
import { useAccessesStore } from "../../../../core/stores/AccessesStore";
import OptionsCard from "./OptionsCard";
import { flattenMenuItems } from "../../../../util/UtilFormat";
import { useLocale } from "../../../../core/hooks/useLocale";

export default function HomeCard(props) {
  const {
    item,
    index,
    handleCardClick,
    headerImg,
    cardSize,
    sectionIndex,
    sectionHidden,
    itemsLength,
  } = props;

  const [locale] = useLocale();

  const { isEditing } = useAccessesStore((state) => ({
    isEditing: state.isEditing,
  }));

  const { menuItems } = useAccessesStore((state) => ({
    menuItems: state.menuItems,
  }));

  const theme = useTheme();

  const cardSizeToTake = item?.itemSize || cardSize;

  const hidden = item?.hidden || sectionHidden || false;

  const classes = useStyles({
    indColor: item?.color,
    cardSize: cardSizeToTake,
    hidden,
  });

  function resolveCardClassName(cs) {
    if (cs === DASHBOARD_CARD_EXTRA_LARGE) {
      return classes.extraLargeRootCard;
    } else if (cs === DASHBOARD_CARD_EXTRA) {
      return classes.extraRootCard;
    } else if (cs === DASHBOARD_CARD_LARGE) {
      return classes.largeRootCard;
    } else if (cs === DASHBOARD_CARD_MEDIUM) {
      return classes.mediumRootCard;
    } else {
      return classes.smallRootCard;
    }
  }

  const allCount = useMemo(() => {
    let result = 0;

    if (!_.isNil(item.active)) {
      result += item.active;
    }

    if (!_.isNil(item.near)) {
      result += item.near;
    }

    if (!_.isNil(item.caducate)) {
      result += item.caducate;
    }

    return result;
  }, [item]);

  const handleCardClickEditing = useCallback(
    (item, headerImg) => {
      if (isEditing) {
        return;
      }
      handleCardClick(item, headerImg);
    },
    [isEditing, handleCardClick]
  );

  const propTextToUse = useMemo(() => {
    if (_.isNil(menuItems) || _.isEmpty(menuItems)) {
      return item?.propText;
    }

    const flattenedMenuItems = flattenMenuItems(menuItems);
    if (_.isNil(flattenedMenuItems) || _.isEmpty(flattenedMenuItems)) {
      return item?.propText;
    }

    const originalItem = _.find(flattenMenuItems(menuItems), {
      route: item?.route,
    });
    if (
      _.isNil(originalItem) ||
      _.isEmpty(originalItem) ||
      !_.isObject(originalItem) ||
      !_.has(originalItem, "propText")
    ) {
      return item?.propText;
    }

    return originalItem?.propText[locale] || item?.propText;
  }, [item, menuItems, locale]);

  return (
    <Grow in={true} timeout={isEditing ? 0 : 300 * index + 1000}>
      <Grid item key={index + 100}>
        <Card
          key={item.key}
          className={resolveCardClassName(cardSizeToTake)}
          onClick={() =>
            handleCardClickEditing(
              { ...item, propText: propTextToUse },
              headerImg
            )
          }
        >
          <CardContent className={classes.cardContent}>
            <div className={classes.cardContentDiv}>
              <Icon
                className={item.icon}
                style={{
                  color: theme?.palette?.header?.textColor,
                  fontSize: 20,
                  width: "40px",
                  height: "40px",
                  marginRight: "10px",
                  backgroundColor: hidden
                    ? "grey"
                    : item?.color || theme?.palette?.content.mainColor,
                  borderRadius: "50px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              />
              {allCount > 0 ? (
                <Typography variant="h6" component="h3">
                  {propTextToUse} ({allCount})
                </Typography>
              ) : (
                <Typography variant="h5" component="h2">
                  {propTextToUse}
                </Typography>
              )}

              <div className={classes.cardIndContainer}>
                {!_.isNil(item.active) && item.active > 0 && (
                  <div className={classes.commonCenteredContainer}>
                    <Icon
                      className="fas fa-dot-circle"
                      style={{
                        color: "#00b894",
                        fontSize: 18,
                        width: "30px",
                        height: "30px",
                        marginRight: "10px",
                        padding: "5px",
                        borderRadius: "50px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    />
                    <Typography variant="subtitle1" component="sub">
                      {item.active}
                    </Typography>
                  </div>
                )}
                {!_.isNil(item.near) && item.near > 0 && (
                  <div className={classes.commonCenteredContainer}>
                    <Icon
                      className="fas fa-hourglass-half"
                      style={{
                        color: "#fffa65",
                        fontSize: 18,
                        width: "30px",
                        height: "30px",
                        marginRight: "10px",
                        padding: "5px",
                        borderRadius: "50px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    />
                    <Typography variant="subtitle1" component="sub">
                      {item.near}
                    </Typography>
                  </div>
                )}
                {!_.isNil(item.caducate) && item.caducate > 0 && (
                  <div className={classes.commonCenteredContainer}>
                    <Icon
                      className="fas fa-power-off"
                      style={{
                        color: theme.palette.content.niceRed,
                        fontSize: 18,
                        width: "30px",
                        height: "30px",
                        marginRight: "10px",
                        padding: "5px",
                        borderRadius: "50px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    />
                    <Typography variant="subtitle1" component="sub">
                      {item.caducate}
                    </Typography>
                  </div>
                )}
                {isEditing && (
                  <OptionsCard
                    currentSize={cardSizeToTake}
                    cardIndex={index}
                    sectionIndex={sectionIndex}
                    isKpi={false}
                    sectionHidden={sectionHidden}
                    hidden={hidden}
                    color={item?.color}
                    itemsLength={itemsLength}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </Grid>
    </Grow>
  );
}
